import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { getViewConfig } from "../../actions";
import { EMPTY_VIEW_CONFIG, withDefaults } from "../../utils/viewConfig";
import { isDatabaseUnreachable, unreachableMessage } from "../../utils/databaseOffline";

// Open pages re-read the configuration this often, and whenever the window
// regains focus or a hidden tab becomes visible, so an admin's change shows
// without a reload. A hidden tab doesn't poll.
export const REFRESH_MS = 30 * 1000;
// A focus event this soon after the last completed fetch is ignored - the
// window was only away for a moment, not long enough to need a fresh copy.
export const FOCUS_MIN_GAP_MS = 5 * 1000;

const ViewConfigContext = createContext({ config: EMPTY_VIEW_CONFIG, unavailable: null, refresh: () => Promise.resolve() });

// Changes only when something users see changed, so pages don't redraw every 30 s.
const signatureOf = (content) => JSON.stringify({ ...content, loadedAt: undefined });

export function ViewConfigProvider({ database, children }) {
  // Tagged with the database it was fetched for. A render that happens
  // between a database switch and the effect below catching up must not
  // show the previous database's configuration - see `config` below.
  const [state, setState] = useState({ database, config: EMPTY_VIEW_CONFIG });
  // Set while the configuration could not be read at all because the database
  // can't be reached: { reason, msg }. A screen with no types must be able to
  // tell "nothing is configured for this" from "nothing could be asked", so it
  // doesn't send the reader to a settings screen over an outage.
  const [unavailable, setUnavailable] = useState(null);
  const generation = useRef(0);
  const signature = useRef("");
  const inFlight = useRef(null);
  const lastFetchAt = useRef(0);

  const refresh = useCallback(() => {
    if (!database) return Promise.resolve();
    // At most one request in flight: a focus event or the interval firing
    // while one is already pending joins it instead of starting another.
    if (inFlight.current) return inFlight.current;
    const id = generation.current;
    const promise = (async () => {
      try {
        const res = await getViewConfig(database);
        lastFetchAt.current = Date.now();
        // A newer database (or an unmount) has since taken over.
        if (id !== generation.current) return;
        // The database can't be reached: remember why, so the screens say so.
        // A copy already on the page stays - it is still what this database
        // shows - but the reason travels beside it.
        if (isDatabaseUnreachable(res)) {
          setUnavailable({ database, reason: res.reason, msg: unreachableMessage(res) });
          return;
        }
        // A failed refresh keeps what the page already shows.
        if (!res?.success || !res.content) return;
        setUnavailable(null);
        // Defends against a stale answer for a database no longer requested,
        // even if it somehow slipped past the generation check above.
        if (res.content.database && res.content.database !== database) return;
        const next = signatureOf(res.content);
        if (next === signature.current) return;
        signature.current = next;
        setState({ database, config: withDefaults(res.content) });
      } finally {
        if (inFlight.current === promise) inFlight.current = null;
      }
    })();
    inFlight.current = promise;
    return promise;
  }, [database]);

  useEffect(() => {
    generation.current += 1;
    signature.current = "";
    lastFetchAt.current = 0;
    inFlight.current = null;
    setState({ database, config: EMPTY_VIEW_CONFIG });
    setUnavailable(null);
    if (!database) return undefined;
    refresh();
    const onFocus = () => {
      if (Date.now() - lastFetchAt.current < FOCUS_MIN_GAP_MS) return;
      refresh();
    };
    const onVisibility = () => {
      if (!document.hidden) onFocus();
    };
    const poll = () => {
      if (!document.hidden) refresh();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    const timer = setInterval(poll, REFRESH_MS);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
      clearInterval(timer);
    };
  }, [database, refresh]);

  // Never show a stale database's configuration while the effect above
  // catches up with a database switch.
  const config = state.database === database ? state.config : EMPTY_VIEW_CONFIG;
  const outage = unavailable && unavailable.database === database ? unavailable : null;
  const value = useMemo(() => ({ config, unavailable: outage, refresh }), [config, outage, refresh]);
  return <ViewConfigContext.Provider value={value}>{children}</ViewConfigContext.Provider>;
}

// A fixed configuration: the admin's live preview, and tests.
export function StaticViewConfigProvider({ config, children }) {
  const value = useMemo(() => ({ config: withDefaults(config), unavailable: null, refresh: () => Promise.resolve() }), [config]);
  return <ViewConfigContext.Provider value={value}>{children}</ViewConfigContext.Provider>;
}

export const useViewConfig = () => useContext(ViewConfigContext).config;
export const useViewConfigRefresh = () => useContext(ViewConfigContext).refresh;
// Why there is no configuration, when the reason is that the database can't
// be reached: { database, reason, msg }, else null.
export const useViewConfigUnavailable = () => useContext(ViewConfigContext).unavailable;
